import React, { useEffect, useState } from 'react';
import { api, AED, AED_short } from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine } from 'recharts';
import { TrendingDown, TrendingUp } from 'lucide-react';

export default function CashFlow() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/cashflow/forecast', { params: { weeks: 8 } }).then(r => setData(r.data));
  }, []);

  if (!data) return <div className="p-10">Loading…</div>;

  const chartData = data.weeks.map(w => ({
    week: w.week,
    Outflow: -w.outflow,
    Inflow: w.inflow,
    Net: w.net,
  }));

  return (
    <div className="py-6 max-w-6xl mx-auto space-y-6" data-testid="cashflow-page">
      <div>
        <div className="overline">Finance</div>
        <h1 className="font-serif text-4xl">Cash flow forecast</h1>
        <p className="text-slate-600 mt-2">Next 8 weeks — projected outflows (pending invoices) and inflows (listed properties).</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass p-5"><div className="overline flex items-center gap-1"><TrendingDown size={11}/> Total outflow (8w)</div><div className="font-serif text-3xl mt-2 text-red-600">{AED_short(data.total_outflow)}</div></div>
        <div className="glass p-5"><div className="overline flex items-center gap-1"><TrendingUp size={11}/> Total inflow (8w)</div><div className="font-serif text-3xl mt-2 text-emerald-700">{AED_short(data.total_inflow)}</div></div>
        <div className="glass p-5"><div className="overline">Net (8w)</div><div className={`font-serif text-3xl mt-2 ${data.total_inflow - data.total_outflow > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{AED_short(data.total_inflow - data.total_outflow)}</div></div>
      </div>

      <div className="glass p-6">
        <div className="font-serif text-2xl mb-4">Weekly view</div>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#64748B' }}/>
              <YAxis tick={{ fontSize: 12, fill: '#64748B' }}/>
              <Tooltip contentStyle={{ borderRadius: 16, border: 'none', boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }} formatter={(v)=>AED_short(Math.abs(v))}/>
              <ReferenceLine y={0} stroke="#94A3B8" />
              <Bar dataKey="Outflow" fill="#EF4444" radius={[6,6,0,0]} />
              <Bar dataKey="Inflow" fill="#10B981" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="glass p-6">
        <div className="font-serif text-2xl mb-4">Week-by-week</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="overline text-left border-b border-slate-200/60">
              <th className="py-2">Week</th><th className="text-right">Outflow</th><th className="text-right">Inflow</th><th className="text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {data.weeks.map(w => (
              <tr key={w.week} className="border-b border-slate-100/70">
                <td className="py-2 font-mono text-xs">{w.week}</td>
                <td className="text-right text-red-600">{w.outflow ? AED(w.outflow) : '—'}</td>
                <td className="text-right text-emerald-700">{w.inflow ? AED(w.inflow) : '—'}</td>
                <td className={`text-right font-semibold ${w.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{AED(w.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
